export interface TreeRuntimeHydrationRow {
  readonly id?: unknown;
  readonly owner_user_id?: unknown;
}

export interface TreeRuntimeEffectiveGraphHydrationInput<
  T extends TreeRuntimeHydrationRow,
> {
  readonly rows: readonly T[];
  readonly targetOwnerUserId: string;
}

export interface TreeRuntimeEffectiveGraphHydrationResult<
  T extends TreeRuntimeHydrationRow,
> {
  readonly acceptedRows: readonly T[];
  readonly acceptedRowCount: number;
  readonly rejectedCrossGraphRowCount: number;
  readonly rejectedInvalidRowCount: number;
  readonly duplicateRowCount: number;
  readonly boundarySatisfied: boolean;
}

export const enforceTreeRuntimeEffectiveGraphHydrationBoundary = <
  T extends TreeRuntimeHydrationRow,
>(
  input: TreeRuntimeEffectiveGraphHydrationInput<T>,
): TreeRuntimeEffectiveGraphHydrationResult<T> => {
  const targetOwnerUserId =
    input.targetOwnerUserId.trim();

  if (!targetOwnerUserId) {
    return Object.freeze({
      acceptedRows: Object.freeze([]),
      acceptedRowCount: 0,
      rejectedCrossGraphRowCount: 0,
      rejectedInvalidRowCount:
        input.rows.length,
      duplicateRowCount: 0,
      boundarySatisfied: false,
    });
  }

  const acceptedRows: T[] = [];
  const seenIds = new Set<string>();

  let rejectedCrossGraphRowCount = 0;
  let rejectedInvalidRowCount = 0;
  let duplicateRowCount = 0;

  for (const row of input.rows) {
    const id =
      typeof row?.id === 'string'
        ? row.id.trim()
        : '';

    const ownerUserId =
      typeof row?.owner_user_id === 'string'
        ? row.owner_user_id.trim()
        : '';

    if (!id || !ownerUserId) {
      rejectedInvalidRowCount += 1;
      continue;
    }

    if (ownerUserId !== targetOwnerUserId) {
      rejectedCrossGraphRowCount += 1;
      continue;
    }

    if (seenIds.has(id)) {
      duplicateRowCount += 1;
      continue;
    }

    seenIds.add(id);
    acceptedRows.push(row);
  }

  return Object.freeze({
    acceptedRows:
      Object.freeze([...acceptedRows]),
    acceptedRowCount:
      acceptedRows.length,
    rejectedCrossGraphRowCount,
    rejectedInvalidRowCount,
    duplicateRowCount,
    boundarySatisfied:
      rejectedCrossGraphRowCount === 0 &&
      rejectedInvalidRowCount === 0,
  });
};
